import { useCallback, useRef, useState } from 'react'
import { BellIcon, CloseIcon, CopyIcon, SyncIcon } from '@/shared/ui/icons'
import type { Toast, ToastVariant } from './toastStore'
import { cn } from '@/shared/lib/cn'

/** Borda ~70% mais grossa que border-2 (2px → 3.4px). Cores theme-aware para dark mode. */
const VARIANT_STYLES: Record<ToastVariant, { bg: string; border: string; accent: string }> = {
  success: {
    bg: 'bg-[var(--color-mint-soft)]',
    border: 'border-[3.4px] border-[var(--color-mint-strong)]',
    accent: 'bg-[var(--color-mint-strong)]',
  },
  info: {
    bg: 'bg-[var(--color-toast-info-bg)]',
    border: 'border-[3.4px] border-[var(--color-toast-info-border)]',
    accent: 'bg-[var(--color-toast-info-accent)]',
  },
  error: {
    bg: 'bg-[var(--color-wine-soft)]',
    border: 'border-[3.4px] border-[var(--color-wine-soft-border)]',
    accent: 'bg-[var(--color-wine-strong)]',
  },
  warning: {
    bg: 'bg-[var(--color-toast-warning-bg)]',
    border: 'border-[3.4px] border-[var(--color-toast-warning-border)]',
    accent: 'bg-[var(--color-warning)]',
  },
  generic: {
    bg: 'bg-[var(--color-mint-soft)]',
    border: 'border-[3.4px] border-[var(--color-border)]',
    accent: 'bg-[var(--color-text-muted)]',
  },
  backToExit: {
    bg: 'bg-gray-600',
    border: 'border-[3.4px] border-gray-500',
    accent: 'bg-[var(--color-bg-surface)]',
  },
}

const SWIPE_THRESHOLD_PX = 80

interface ToastItemProps {
  toast: Toast
  onDismiss: (id: string) => void
}

export const ToastItem = ({ toast, onDismiss }: ToastItemProps) => {
  const [translateX, setTranslateX] = useState(0)
  const touchStartX = useRef(0)
  const lastTranslateX = useRef(0)

  const styles = VARIANT_STYLES[toast.variant]
  const isBackToExit = toast.variant === 'backToExit'

  const handleDismiss = useCallback(() => {
    setTranslateX(-200)
    requestAnimationFrame(() => {
      setTimeout(() => onDismiss(toast.id), 150)
    })
  }, [toast.id, onDismiss])

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchStartX.current
    const next = Math.max(Math.min(dx, 0), -200)
    lastTranslateX.current = next
    setTranslateX(next)
  }

  const handleTouchEnd = () => {
    if (lastTranslateX.current < -SWIPE_THRESHOLD_PX) {
      setTranslateX(-200)
      requestAnimationFrame(() => {
        setTimeout(() => onDismiss(toast.id), 150)
      })
    } else {
      setTranslateX(0)
    }
  }

  const handleActionClick = () => {
    toast.action?.onClick()
    onDismiss(toast.id)
  }

  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(async () => {
    const text = toast.title ? `${toast.title}\n${toast.message}` : toast.message
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }, [toast.title, toast.message])

  const isPush = Boolean(toast.title)
  const tapToAction = Boolean(toast.tapToAction && toast.action)
  const isError = toast.variant === 'error'

  const content = (
    <>
      {toast.title ? (
        <BellIcon
          size={20}
          className="shrink-0 self-start text-[var(--color-toast-info-accent)]"
          aria-hidden
        />
      ) : !isBackToExit ? (
        <span className={cn('h-1.5 w-1.5 shrink-0 self-center rounded-full', styles.accent)} aria-hidden />
      ) : null}
      <div className="min-w-0 flex-1">
        {toast.title ? (
          <>
            <p className="text-sm font-semibold leading-tight text-[var(--color-text-primary)]">
              {toast.title}
            </p>
            {toast.message ? (
              <p className="mt-0.5 text-xs leading-snug text-[var(--color-text-secondary)] line-clamp-4">
                {toast.message}
              </p>
            ) : null}
          </>
        ) : (
          <p
            className={cn(
              'text-sm font-medium leading-snug line-clamp-4 md:text-xs',
              isBackToExit ? 'text-white' : 'text-[var(--color-text-primary)]',
            )}
          >
            {toast.message}
          </p>
        )}
      </div>
      {tapToAction ? (
        <SyncIcon
          size={22}
          className="shrink-0 self-center text-[var(--color-teal-ink)]"
          aria-hidden
        />
      ) : toast.action ? (
        <button
          type="button"
          onClick={handleActionClick}
          className={cn(
            'shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition-colors md:px-2 md:py-0.5 md:text-[11px]',
            toast.action.style === 'primary'
              ? 'bg-[var(--color-mint-strong)] text-white hover:bg-[var(--color-mint-strong)]/90'
              : 'text-[var(--color-link)] hover:bg-[var(--color-mint-strong)]/10 hover:underline',
          )}
        >
          {toast.action.label}
        </button>
      ) : null}
      {!tapToAction && !isBackToExit ? (
        <div className="flex shrink-0 items-center gap-0.5">
          {isError ? (
            <button
              type="button"
              onClick={handleCopy}
              className="-mr-0.5 -mt-0.5 flex rounded-lg p-2 text-[var(--color-wine-strong)] transition-colors hover:bg-black/5 [data-theme=dark]:hover:bg-[var(--color-bg-surface)]/10 md:p-1.5"
              aria-label={copied ? 'Copiado' : 'Copiar conteúdo'}
              title={copied ? 'Copiado' : 'Copiar conteúdo'}
            >
              <CopyIcon size={20} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleDismiss}
            className="-mr-0.5 -mt-0.5 flex rounded-lg p-2 text-[var(--color-text-muted)] transition-colors hover:bg-black/5 hover:text-[var(--color-text-primary)] [data-theme=dark]:hover:bg-[var(--color-bg-surface)]/10 md:p-1.5"
            aria-label="Fechar"
          >
            <CloseIcon size={20} />
          </button>
        </div>
      ) : null}
    </>
  )

  const baseClassName = cn(
    'flex min-w-0 gap-3 rounded-xl border shadow-[0_10px_24px_rgba(31,43,40,0.14)] transition-all duration-200',
    '[data-theme=dark]:shadow-[0_10px_24px_rgba(0,0,0,0.4)]',
    isPush
      ? 'max-w-md min-w-[300px] items-start px-5 py-4 md:px-4 md:py-3'
      : isBackToExit
        ? 'max-w-xs items-center gap-2 px-3 py-2'
        : 'max-w-sm items-center gap-2 px-4 py-3 md:px-3 md:py-2',
    styles.bg,
    styles.border,
    translateX < -150 && 'opacity-0',
    tapToAction && 'cursor-pointer active:opacity-90',
  )

  if (tapToAction) {
    return (
      <button
        type="button"
        onClick={handleActionClick}
        role="status"
        aria-live="polite"
        aria-label="Atualizar o sistema"
        className={baseClassName}
        style={{ transform: `translateX(${translateX}px)` }}
      >
        {content}
      </button>
    )
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={baseClassName}
      style={{ transform: `translateX(${translateX}px)` }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {content}
    </div>
  )
}